import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Eye,
  BarChart3,
  ShoppingCart,
} from "lucide-react";
import { useAuth } from "../../context/Auth";
import ExportData from "../../components/common/ExportData";
import SortableTableHeader from "../../components/Table/SortableTableHeader";

const TodaySales = () => {
  const { requestWithAuth } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [tableData, setTableData] = useState([])

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const sortParam = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : "";

  const fetchSales = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/report/sales/today?page=${current_page}&pageSize=${page_size}&search=${search_query}${sortParam}`,
        "GET"
      );
      setSales(data?.results || []);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / page_size));
    } catch (error) {
      console.error("Error fetching sales data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async (page_size, search_query, current_page) => {

    try {
      const data = await requestWithAuth(
        `/report/sales/today?page=${current_page}&pageSize=100000000000000000&search=${search_query}${sortParam}`,
        "GET"
      );
      setTableData(data?.results || []);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    }
  };



  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSales(pageSize, search, currentPage);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, currentPage, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchTableData(pageSize, search, currentPage);
  }, []);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[32px] h-[32px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-600 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <span>POS</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Reports</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Today's Sales</h1>
              <p className="text-gray-600 text-sm">
                Overview of sales performance for the current day
              </p>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Controls */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search sales..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Page Size & Actions */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Export Buttons (Placeholders styled) */}
                <div className="flex">
                  <ExportData tableData={tableData} fileName="sales" />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span>Loading sales data...</span>
                </div>
              </div>
            ) : sales?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch Code
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch Name
                    </th>
                    <SortableTableHeader
                      label="Customers"
                      fieldKey="customers_served"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Items Qty"
                      fieldKey="item_quantity"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Total Amount"
                      fieldKey="total_amount"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Profit"
                      fieldKey="profit"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <th className="text-right py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sales.map((sale, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        #{sale?.id}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {sale?.name}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {sale?.customers_served}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {Number(sale?.item_quantity || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {Number(sale?.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm text-green-600 font-medium">
                        {Number(sale?.profit || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <Link
                          to={`/sales/today/${sale?.id}/`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                        >
                          <Eye size={14} />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-3">
                  <ShoppingCart className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No Sales Found</h3>
                <p className="text-gray-500 text-sm mt-1">
                  We couldn't find any sales records for today.
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {sales?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodaySales;
